import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logAnalyticsEvent } from './logAnalyticsEvent';
import { shouldSkipAnalytics } from './analyticsActor';
import {
  canFlushFirstVisitExit,
  isFirstVisitBrowser,
  markFirstVisitExitFlushed,
  markHasVisited,
} from './firstVisit';
import {
  DWELL_MAX_MS,
  DWELL_MIN_MS,
  buildFirstVisitExitFields,
  noteFirstVisitPageEnter,
  normalizePageKey,
  trackUxEvent,
} from '../services/uxEvents';

/**
 * Logs SPA `page_view` (GA), visible-time `page_dwell` (uxEvents),
 * and one `first_visit_exit` with isLastAction for first-time browsers.
 * Must be rendered inside a BrowserRouter.
 * Admins are excluded entirely (`shouldSkipAnalytics`).
 */
export const PageViewTracker = () => {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  const visibleStartedAtRef = useRef(
    typeof document === 'undefined' || document.visibilityState === 'visible'
      ? Date.now()
      : null
  );
  const accumulatedMsRef = useRef(0);

  useEffect(() => {
    const flushDwell = (pathname) => {
      if (shouldSkipAnalytics()) return;
      let ms = accumulatedMsRef.current;
      if (
        visibleStartedAtRef.current != null &&
        (typeof document === 'undefined' || document.visibilityState === 'visible')
      ) {
        ms += Date.now() - visibleStartedAtRef.current;
      }
      accumulatedMsRef.current = 0;
      visibleStartedAtRef.current =
        typeof document === 'undefined' || document.visibilityState === 'visible'
          ? Date.now()
          : null;

      const durationMs = Math.min(DWELL_MAX_MS, Math.floor(ms));
      if (durationMs < DWELL_MIN_MS) return;

      const pageKey = normalizePageKey(pathname);
      trackUxEvent({
        name: 'page_dwell',
        pathname,
        pageKey,
        durationMs,
      });
    };

    const flushFirstVisitExit = () => {
      if (shouldSkipAnalytics()) return;
      if (!isFirstVisitBrowser()) return;
      const fields = buildFirstVisitExitFields();
      if (!fields?.visitId || !canFlushFirstVisitExit(fields.visitId)) return;
      markFirstVisitExitFlushed(fields.visitId);
      markHasVisited();
      trackUxEvent(fields);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (visibleStartedAtRef.current != null) {
          accumulatedMsRef.current += Date.now() - visibleStartedAtRef.current;
          visibleStartedAtRef.current = null;
        }
        flushDwell(pathnameRef.current);
        flushFirstVisitExit();
      } else if (document.visibilityState === 'visible') {
        visibleStartedAtRef.current = Date.now();
      }
    };

    const onPageHide = () => {
      if (visibleStartedAtRef.current != null) {
        accumulatedMsRef.current += Date.now() - visibleStartedAtRef.current;
        visibleStartedAtRef.current = null;
      }
      flushDwell(pathnameRef.current);
      flushFirstVisitExit();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  useEffect(() => {
    const prevPath = pathnameRef.current;
    if (prevPath !== location.pathname) {
      let ms = accumulatedMsRef.current;
      if (visibleStartedAtRef.current != null) {
        ms += Date.now() - visibleStartedAtRef.current;
      }
      accumulatedMsRef.current = 0;
      visibleStartedAtRef.current =
        document.visibilityState === 'visible' ? Date.now() : null;

      const durationMs = Math.min(DWELL_MAX_MS, Math.floor(ms));
      if (durationMs >= DWELL_MIN_MS && !shouldSkipAnalytics()) {
        trackUxEvent({
          name: 'page_dwell',
          pathname: prevPath,
          pageKey: normalizePageKey(prevPath),
          durationMs,
        });
      }
      pathnameRef.current = location.pathname;
    }

    if (shouldSkipAnalytics()) return;

    logAnalyticsEvent('page_view', {
      page_path: location.pathname + location.search,
      page_title: typeof document !== 'undefined' ? document.title : '',
    });
    noteFirstVisitPageEnter(location.pathname);
  }, [location.pathname, location.search]);

  return null;
};
